import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CandidateLinkShell, CandidateLinkSpinner, ExpiredLink } from '../components/CandidateLinkShell'
import { configureCandidateLinkApi } from '../services/candidateLinkRoute'
import { ChecklistQuestion, getChecklist, submitChecklist } from '../services/candidateLinks'

type Status = 'loading' | 'ready' | 'done' | 'gone'

/** A compatibility page for an unexpired checklist link issued before one-link. */
const CandidateChecklist: React.FC = () => {
  const { region, code } = useParams<{ region: string; code: string }>()
  const [status, setStatus] = useState<Status>('loading')
  const [questions, setQuestions] = useState<ChecklistQuestion[]>([])
  const [answers, setAnswers] = useState<Record<number, string>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!code || !configureCandidateLinkApi(region)) {
      setStatus('gone')
      return
    }
    try {
      const checklist = await getChecklist(code)
      setQuestions(checklist.questions)
      setStatus(checklist.complete ? 'done' : 'ready')
    } catch {
      setStatus('gone')
    }
  }, [code, region])

  useEffect(() => {
    load()
  }, [load])

  const save = async () => {
    if (!code || saving) return
    const filled = questions
      .filter((question) => (answers[question.id] || '').trim())
      .map((question) => ({ question_id: question.id, answer: answers[question.id].trim() }))
    if (!filled.length) return

    setSaving(true)
    setError(null)
    try {
      const result = await submitChecklist(code, filled)
      setQuestions(result.questions)
      setAnswers({})
      if (result.complete) setStatus('done')
    } catch {
      setError('We could not save that just now. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (status === 'loading') {
    return <CandidateLinkShell><CandidateLinkSpinner /></CandidateLinkShell>
  }
  if (status === 'gone') return <ExpiredLink code={code!} />
  if (status === 'done') {
    return (
      <CandidateLinkShell>
        <h1 className="text-2xl">All done, thank you</h1>
        <p className="mt-4 text-base" style={{ color: 'var(--link-ink-soft)' }}>
          We have everything we need from this form. Your recruiter will be in touch
          with the next step.
        </p>
      </CandidateLinkShell>
    )
  }

  return (
    <CandidateLinkShell>
      <h1 className="text-2xl">A few questions</h1>
      <div className="mt-8 space-y-7">
        {questions.map((question) => (
          <div key={question.id}>
            <label htmlFor={`q-${question.id}`} className="block text-sm font-medium leading-snug">
              {question.question}
            </label>
            {(question.options?.length ?? 0) > 0 ? (
              <div className="mt-3 space-y-2">
                {question.options!.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={answers[question.id] === option}
                    data-chosen={answers[question.id] === option ? 'true' : undefined}
                    onClick={() => setAnswers((current) => ({ ...current, [question.id]: option }))}
                    className="orbio-link-choice w-full rounded-xl px-4 py-3.5 text-left text-base"
                  >
                    {option}
                  </button>
                ))}
              </div>
            ) : (
              <textarea
                id={`q-${question.id}`}
                rows={3}
                value={answers[question.id] || ''}
                onChange={(event) => setAnswers((current) => ({ ...current, [question.id]: event.target.value }))}
                className="orbio-link-field mt-2 w-full rounded-xl p-3 text-base"
              />
            )}
          </div>
        ))}
      </div>
      {error && <p className="mt-4 text-sm" style={{ color: '#a33' }}>{error}</p>}
      <button
        type="button"
        onClick={save}
        disabled={saving || !Object.values(answers).some((answer) => answer.trim())}
        className="orbio-link-button mt-8 w-full rounded-xl px-4 py-3.5 text-base font-medium"
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </CandidateLinkShell>
  )
}

export default CandidateChecklist
